import { useSuspenseQuery } from "@tanstack/react-query";
import {
    AlgoliaIndexes,
    DepartureHit,
    departuresFilterToFacet,
    getAlgoliaSearch,
} from "@wojo/services";
import { SearchResults } from "algoliasearch-helper";
import dayjs from "dayjs";
import {
    parseAsArrayOf,
    parseAsBoolean,
    parseAsIsoDateTime,
    ParserBuilder,
    SetValues,
    useQueryStates,
    Values,
} from "nuqs";
import React, { createContext, useContext, useState } from "react";
import { useTourPageContext } from "../../../_client/context";
import { parseAsEncodedString } from "../../search-params";

const attributesToRetrieve = [
    "activityLevel",
    "assets",
    "bookableExtensionSpots",
    "bookableSpots",
    "cardImage",
    "classLevel",
    "dateRangeLabel",
    "departureStatusBadge",
    "departureDate",
    "discountAmount",
    "endDate",
    "estimatedBiweeklyPayment",
    "extensionName",
    "gateways",
    "hasExtension",
    "hasExtensionAvailable",
    "id",
    "isExtensionHidden",
    "isOnSale",
    "name",
    "nbCities",
    "nbDays",
    "nbDaysBeforeDeparture",
    "nbDaysWithExtension",
    "nbSpotsLeft",
    "price",
    "salePrice",
    "specialDepartureContent",
    "status",
    "tourBadge",
    "tourCode",
    "tourPagePath",
];

type NuqsFilters = {
    departureStartDate: ParserBuilder<Date>;
    departureEndDate: ParserBuilder<Date>;
    filters: ParserBuilder<string[]>;
    tripTypes: ParserBuilder<string[]>;
    priceWithFlights: ParserBuilder<boolean>;
    priceWithFlightsGateway: ParserBuilder<string>;
};

export type DeparturesStepContextValue = {
    isFilterOpen?: boolean;
    setIsFilterOpen?: React.Dispatch<React.SetStateAction<boolean>>;
    departures: {
        hits: DepartureHit[];
        page: number;
        nbPages: number;
        nbHits: number;
        hitsPerPage: number;
        facets: (SearchResults.Facet & { data: unknown })[];
        disjunctiveFacets: (SearchResults.Facet & { data: unknown })[];
    };
    withFlightsPriceGateways: DepartureHit["gateways"];
    isLoading: boolean;
    setFilterStates: SetValues<NuqsFilters>;
    filterStates: Values<NuqsFilters>;
};

const DeparturesStepContext = createContext<DeparturesStepContextValue>(null!);

export const DeparturesStepContextProvider: React.FC<
    React.PropsWithChildren
> = ({ children }) => {
    const { tourCodes = [] } = useTourPageContext();
    const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
    const [filterStates, setFilterStates] = useQueryStates<NuqsFilters>({
        departureStartDate: parseAsIsoDateTime,
        departureEndDate: parseAsIsoDateTime,
        filters: parseAsArrayOf(parseAsEncodedString), // these are miscellaneous filters that fall under the same Checkboxes group
        tripTypes: parseAsArrayOf(parseAsEncodedString),
        priceWithFlights: parseAsBoolean,
        priceWithFlightsGateway: parseAsEncodedString,
    });
    const { search } = getAlgoliaSearch({
        baseIndex: AlgoliaIndexes.DEPARTURES,
        disjunctiveFacets: [
            "hasExtensionAvailable",
            "isOnSale",
            "tourCode",
            "status",
            "classLevel.name",
            "departureDate",
            "gateways.displayName",
        ],
        filterToFacetMapper: departuresFilterToFacet,
        attributesToRetrieve: [
            ...attributesToRetrieve,
            `gatewayPricing.${filterStates.priceWithFlightsGateway}`,
        ],
    });
    const { departureStartDate, filters, tripTypes, departureEndDate } =
        filterStates;

    const gatewayName =
        filterStates.priceWithFlights && filterStates.priceWithFlightsGateway;

    const { data, isLoading } = useSuspenseQuery({
        queryKey: [
            "departures",
            ...tourCodes,
            ...(filters ?? []),
            ...(tripTypes ?? []),
            departureStartDate,
            departureEndDate,
            gatewayName,
        ],
        refetchOnReconnect: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        queryFn: () =>
            search({
                filters: {
                    tourCode: tourCodes,
                    ...(filters?.includes("isOnSale") && {
                        isOnSale: [true],
                    }),
                    ...(filters?.includes("hasExtensionAvailable") && {
                        hasExtensionAvailable: [true],
                    }),
                    ...(filters?.includes("status") && {
                        status: ["Available"],
                    }),
                    ...(tripTypes?.length && {
                        tripType: tripTypes,
                    }),
                    ...(!!gatewayName && {
                        gatewayName: gatewayName,
                    }),
                    dates: {
                        from: departureStartDate ?? dayjs().toDate(),
                        to: departureEndDate
                            ? dayjs(departureEndDate).toDate()
                            : undefined,
                    },
                },
                hitsPerPage: 1000,
                page: 0,
            }),
    });

    const withFlightsPriceGatewayDisplayNames = Object.keys(
        data.disjunctiveFacets.find(
            (facet: SearchResults.Facet) =>
                facet.name === "gateways.displayName",
        )?.data ?? {},
    );

    const flattenedGateways = (data.hits as DepartureHit[]).flatMap(
        (hit) => hit.gateways,
    );
    const withFlightsPriceGateways = withFlightsPriceGatewayDisplayNames.reduce(
        (acc, gatewayDisplayName) => {
            const gateway = flattenedGateways.find(
                (gateway) => gateway?.displayName === gatewayDisplayName,
            );
            if (gateway) {
                acc.push(gateway);
            }
            return acc;
        },
        new Array<{
            code: string;
            state: string | null;
            city: string;
            displayName: string;
        }>(),
    );

    return (
        <DeparturesStepContext.Provider
            value={{
                isFilterOpen,
                setIsFilterOpen,
                departures: data,
                isLoading: isLoading,
                setFilterStates,
                filterStates,
                withFlightsPriceGateways,
            }}
        >
            {children}
        </DeparturesStepContext.Provider>
    );
};

export const useDeparturesStepContext = () => useContext(DeparturesStepContext);
